// =====================================================
// CKAN Hybrid Query Library
// Descripción: Librería transparente para consultar datos CKAN
// desde PostgreSQL o Supabase Storage automáticamente
// =====================================================
// El frontend NO necesita saber dónde están los datos.
// Esta librería lee la estrategia de almacenamiento y obtiene
// los datos de la ubicación correcta.
// =====================================================

import { supabase } from '../supabase';

// =====================================================
// Tipos
// =====================================================

export interface CKANSyncStatus {
  id: string;
  resource_name: string;
  resource_type: 'csv' | 'excel' | 'json' | 'xml' | 'shapefile' | 'metadata';
  resource_label: string | null;
  ckan_resource_id: string | null;
  ckan_url: string | null;
  file_format: string | null;
  file_size_bytes: number | null;
  records_count: number | null;
  status: 'idle' | 'downloading' | 'processing' | 'ready' | 'error';

  // Campos de arquitectura híbrida
  storage_strategy: 'postgresql' | 'storage' | null;
  storage_path_data: string | null;
  storage_path_raw: string | null;
  headers: string[] | null;
  column_types: Record<string, string> | null;
  sample_data: any[] | null;
  sheets: string[] | null; // Excel
  geometry_types: string[] | null; // Shapefile
  bounds: any | null; // Shapefile
  properties: string[] | null; // Shapefile
  is_array: boolean | null; // JSON
  schema: any | null; // JSON

  last_sync_started_at: string | null;
  last_sync_completed_at: string | null;
  downloaded_at: string | null;
  processed_at: string | null;
  last_error: string | null;
  error_count: number;
  sync_metadata: any | null;
  ready_to_process: boolean;
  created_at: string;
  updated_at: string;
}

export interface HybridQueryResult {
  success: boolean;
  data: any[];
  metadata: {
    resource_name: string;
    storage_strategy: 'postgresql' | 'storage';
    headers: string[];
    column_types: Record<string, string>;
    records_count: number;
    file_size_bytes: number;
  };
  error?: string;
}

// =====================================================
// Función Principal: getCKANData
// Obtiene datos de un recurso CKAN de forma transparente
// =====================================================

/**
 * Obtiene los datos de un recurso CKAN.
 * Automáticamente detecta si los datos están en PostgreSQL o Storage
 * y los obtiene de la ubicación correcta.
 *
 * @param resourceName - Nombre del recurso (slug único)
 * @param options - Opciones de consulta (filtros, límites, etc.)
 * @returns Promesa con los datos y metadatos
 */
export async function getCKANData(
  resourceName: string,
  options: {
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
  } = {}
): Promise<HybridQueryResult> {
  try {
    console.log(`[HybridQuery] Fetching data for: ${resourceName}`);

    // 1. Obtener metadatos del recurso desde ckan_sync_status
    const { data: syncStatus, error: statusError } = await supabase
      .from('ckan_sync_status')
      .select('*')
      .eq('resource_name', resourceName)
      .single();

    if (statusError || !syncStatus) {
      throw new Error(`Resource not found: ${resourceName}`);
    }

    const metadata = syncStatus as CKANSyncStatus;

    // Validar que el recurso esté listo
    if (metadata.status !== 'ready') {
      throw new Error(`Resource ${resourceName} is not ready (status: ${metadata.status})`);
    }

    // Validar que haya estrategia de almacenamiento
    if (!metadata.storage_strategy) {
      throw new Error(`Resource ${resourceName} has no storage strategy defined`);
    }

    console.log(`[HybridQuery] Storage strategy: ${metadata.storage_strategy}`);

    let data: any[];

    // 2. Obtener datos según la estrategia
    if (metadata.storage_strategy === 'postgresql') {
      // ========== ESTRATEGIA: PostgreSQL ==========
      console.log(`[HybridQuery] Fetching from PostgreSQL...`);
      data = await fetchFromPostgreSQL(resourceName, options);
    } else if (metadata.storage_strategy === 'storage') {
      // ========== ESTRATEGIA: Storage ==========
      console.log(`[HybridQuery] Fetching from Storage...`);
      data = await fetchFromStorage(metadata.storage_path_data!, options);
    } else {
      throw new Error(`Unknown storage strategy: ${metadata.storage_strategy}`);
    }

    console.log(`[HybridQuery] Fetched ${data.length} records`);

    // 3. Retornar resultado con metadatos
    return {
      success: true,
      data: data,
      metadata: {
        resource_name: resourceName,
        storage_strategy: metadata.storage_strategy,
        headers: metadata.headers || [],
        column_types: metadata.column_types || {},
        records_count: metadata.records_count || 0,
        file_size_bytes: metadata.file_size_bytes || 0,
      },
    };
  } catch (error: any) {
    console.error(`[HybridQuery] Error:`, error);
    return {
      success: false,
      data: [],
      metadata: {
        resource_name: resourceName,
        storage_strategy: 'postgresql',
        headers: [],
        column_types: {},
        records_count: 0,
        file_size_bytes: 0,
      },
      error: error.message,
    };
  }
}

// =====================================================
// Función: fetchFromPostgreSQL
// Obtiene datos desde ckan_data_generic
// =====================================================
async function fetchFromPostgreSQL(
  resourceName: string,
  options: {
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
  }
): Promise<any[]> {
  let query = supabase
    .from('ckan_data_generic')
    .select('data')
    .eq('resource_name', resourceName);

  // Aplicar filtros JSONB si se proporcionan
  if (options.filters) {
    for (const [key, value] of Object.entries(options.filters)) {
      // Filtro JSONB: data->>'key' = 'value'
      query = query.filter('data->>'+key, 'eq', value);
    }
  }

  // Aplicar orden (por defecto por created_at)
  query = query.order('created_at', { ascending: true });

  // Aplicar paginación
  if (options.limit) {
    query = query.limit(options.limit);
  }
  if (options.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 100) - 1);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`PostgreSQL query error: ${error.message}`);
  }

  // Extraer objetos JSONB del campo 'data'
  return data?.map((row: any) => row.data) || [];
}

// =====================================================
// Función: fetchFromStorage
// Obtiene datos desde Supabase Storage
// =====================================================
async function fetchFromStorage(
  storagePath: string,
  options: {
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
  }
): Promise<any[]> {
  // Remover prefijo 'ckan/' si existe
  const cleanPath = storagePath.replace(/^ckan\//, '');

  console.log(`[Hosting Storage] Downloading from: ${cleanPath}`);

  // Descargar JSON desde Hosting Storage
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';
  const url = `${dashboardUrl}/ckan/${cleanPath}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Storage download error (${response.status}): ${response.statusText}`);
  }

  // Parsear JSON
  const jsonText = await response.text();
  const fullData: any[] = JSON.parse(jsonText);

  console.log(`[Storage] Downloaded ${fullData.length} records (${(jsonText.length / 1024).toFixed(2)} KB)`);

  // Aplicar filtros en memoria (si se proporcionan)
  let filteredData = fullData;
  if (options.filters) {
    filteredData = fullData.filter(row => {
      return Object.entries(options.filters!).every(([key, value]) => {
        return row[key] === value;
      });
    });
  }

  // Aplicar paginación en memoria
  const offset = options.offset || 0;
  const limit = options.limit || filteredData.length;

  return filteredData.slice(offset, offset + limit);
}

// =====================================================
// Función: getCKANMetadata
// Obtiene SOLO los metadatos de un recurso (sin datos)
// Útil para previews, validación, etc.
// =====================================================

/**
 * Obtiene solo los metadatos de un recurso CKAN.
 * No descarga los datos completos, solo headers, tipos, sample_data.
 *
 * @param resourceName - Nombre del recurso
 * @returns Promesa con los metadatos
 */
export async function getCKANMetadata(
  resourceName: string
): Promise<CKANSyncStatus | null> {
  const { data, error } = await supabase
    .from('ckan_sync_status')
    .select('*')
    .eq('resource_name', resourceName)
    .single();

  if (error) {
    console.error(`[HybridQuery] Metadata error:`, error);
    return null;
  }

  return data as CKANSyncStatus;
}

// =====================================================
// Función: getAllCKANResources
// Lista todos los recursos CKAN disponibles con sus metadatos
// =====================================================

/**
 * Lista todos los recursos CKAN disponibles.
 *
 * @param filters - Filtros opcionales (tipo, status, etc.)
 * @returns Promesa con array de recursos
 */
export async function getAllCKANResources(
  filters?: {
    resource_type?: string;
    status?: string;
    storage_strategy?: 'postgresql' | 'storage';
  }
): Promise<CKANSyncStatus[]> {
  let query = supabase
    .from('ckan_sync_status')
    .select('*')
    .order('created_at', { ascending: false });

  if (filters?.resource_type) {
    query = query.eq('resource_type', filters.resource_type);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.storage_strategy) {
    query = query.eq('storage_strategy', filters.storage_strategy);
  }

  const { data, error } = await query;

  if (error) {
    console.error(`[HybridQuery] Resources list error:`, error);
    return [];
  }

  return (data as CKANSyncStatus[]) || [];
}

// =====================================================
// Función: getCKANSampleData
// Obtiene solo una muestra de datos (primeras 5 filas)
// Sin descargar el archivo completo si está en Storage
// =====================================================

/**
 * Obtiene una muestra de datos (5 primeras filas).
 * Si los datos están en Storage, usa sample_data de los metadatos
 * en lugar de descargar el archivo completo.
 *
 * @param resourceName - Nombre del recurso
 * @returns Promesa con muestra de datos
 */
export async function getCKANSampleData(
  resourceName: string
): Promise<any[]> {
  const metadata = await getCKANMetadata(resourceName);

  if (!metadata) {
    return [];
  }

  // Si hay sample_data en metadatos, usarlo directamente
  if (metadata.sample_data && metadata.sample_data.length > 0) {
    console.log(`[HybridQuery] Using cached sample data`);
    return metadata.sample_data;
  }

  // Si no hay sample, obtener primeras 5 filas
  const result = await getCKANData(resourceName, { limit: 5 });
  return result.data;
}
